import { useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { clsx } from "clsx";

import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  PageHeader,
  SectionCard,
} from "@/features/power_projects/components/ui";

import { FeedCard } from "../components";
import { useEzFeed } from "../hooks";
import type { FeedParams } from "../types";
import { PROVINCES, Segmented, TAG_LABEL } from "../ui";

/* Multiselect of decision states — default hides the passed-on ones. */
const DEFAULT_DECISIONS = "none,start,watch";
const DECISION_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "Brak decyzji" },
  { value: "start", label: "Startujemy" },
  { value: "watch", label: "Obserwujemy" },
  { value: "pass", label: "Odpuszczamy" },
];

function MultiToggle({
  value,
  onChange,
  options,
  fallback,
  ariaLabel,
  title,
  prefix,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  fallback: string;
  ariaLabel: string;
  title: string;
  prefix?: string;
}) {
  const selected = new Set(value.split(",").filter(Boolean));
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    // an empty selection would mean "nothing" — snap back to the fallback view
    onChange(next.size ? Array.from(next).join(",") : fallback);
  };
  return (
    <div
      className="inline-flex items-stretch rounded-lg overflow-hidden border border-[var(--color-border)] divide-x divide-[var(--color-border)]"
      role="group"
      aria-label={ariaLabel}
      title={title}
    >
      {prefix && (
        <span className="text-[10px] uppercase tracking-wider px-2 py-1.5 text-[var(--color-text-muted)] bg-black/[.03] dark:bg-white/[.05] self-center">
          {prefix}
        </span>
      )}
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => toggle(o.value)}
          className={clsx(
            "text-xs px-2.5 py-1.5 transition-colors",
            selected.has(o.value)
              ? "bg-rose-600 text-white"
              : "bg-transparent text-[var(--color-text-muted)] hover:bg-black/5 dark:hover:bg-white/10 line-through decoration-1",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const DEFAULT_RECOS = "none,start,watch,pass";
const RECO_OPTIONS: { value: string; label: string }[] = [
  { value: "start", label: "Startuj" },
  { value: "watch", label: "Obserwuj" },
  { value: "none", label: "Brak" },
  { value: "pass", label: "Odpuść" },
];

const STAGE_FILTERS: {
  value: NonNullable<FeedParams["stage"]>;
  label: string;
}[] = [
  { value: "", label: "Wszystkie etapy" },
  { value: "s2_qualified", label: "Priorytet" },
  { value: "s1_candidate", label: "Czeka na AI" },
  { value: "s1_classified", label: "Poza profilem" },
  { value: "s1_rejected", label: "Odrzucone przez AI" },
  { value: "rejected_rules", label: "Odrzucone regułami" },
];

export default function EzFeedPage() {
  const [params, setParams] = useState<FeedParams>({
    scope: "open",
    page: 1,
    page_size: 25,
  });
  const [searchDraft, setSearchDraft] = useState("");
  const feed = useEzFeed(params);

  const set = (patch: Partial<FeedParams>) =>
    setParams((p) => ({ ...p, ...patch, page: patch.page ?? 1 }));

  const meta = feed.data?.meta;
  const pages = meta ? Math.max(1, Math.ceil(meta.total / meta.page_size)) : 1;
  const rows = feed.data?.rows ?? [];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Oferty przetargowe"
        subtitle="BZP · ezamowienia.gov.pl — po preselekcji EZ-ARON. Twoja decyzja jest nadrzędna nad oceną AI."
        actions={
          <Link to="/ezamowienia/candidates">
            <Button variant="secondary">Kandydaci AI i load danych →</Button>
          </Link>
        }
      />

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Segmented
          options={[
            { value: "open", label: "Otwarte" },
            { value: "resolved", label: "Rozstrzygnięte" },
            { value: "all", label: "Wszystkie" },
          ]}
          value={params.scope ?? "open"}
          onChange={(scope) => set({ scope })}
        />
        <select
          value={params.stage ?? ""}
          onChange={(e) =>
            set({ stage: e.target.value as FeedParams["stage"] })
          }
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          {STAGE_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <Segmented
          options={[
            { value: "", label: "Wszystkie" },
            { value: "P", label: "P" },
            { value: "PW", label: "P+W" },
            { value: "W", label: "W" },
          ]}
          value={params.work_mode ?? ""}
          onChange={(work_mode) => set({ work_mode })}
        />
        <select
          value={params.tag ?? ""}
          onChange={(e) => set({ tag: e.target.value })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="">Wszystkie branże</option>
          {Object.entries(TAG_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={params.province ?? ""}
          onChange={(e) => set({ province: e.target.value })}
          className="text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent"
        >
          <option value="">Cała Polska</option>
          {PROVINCES.map((p) => (
            <option key={p.code} value={p.code}>
              {p.name}
            </option>
          ))}
        </select>
        <MultiToggle
          prefix="Decyzja"
          value={params.decisions ?? DEFAULT_DECISIONS}
          onChange={(decisions) => set({ decisions })}
          options={DECISION_OPTIONS}
          fallback={DEFAULT_DECISIONS}
          ariaLabel="Pokazywane decyzje"
          title="Twoje decyzje — domyślnie bez odpuszczonych"
        />
        <MultiToggle
          prefix="EZ-ARON"
          value={params.recos ?? DEFAULT_RECOS}
          onChange={(recos) => set({ recos })}
          options={RECO_OPTIONS}
          fallback={DEFAULT_RECOS}
          ariaLabel="Rekomendacje AI"
          title="Rekomendacje EZ-ARON — „Odpuść” i tak spadają na koniec listy"
        />
        <div className="flex items-center gap-1">
          <input
            inputMode="numeric"
            value={params.price_from ?? ""}
            onChange={(e) =>
              set({ price_from: e.target.value.replace(/[^\d]/g, "") })
            }
            placeholder="Cena od"
            className="w-24 text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent tabular-nums"
            title="Wygrana cena od (zł) — dotyczy rozstrzygniętych"
          />
          <span className="text-xs text-[var(--color-text-muted)]">–</span>
          <input
            inputMode="numeric"
            value={params.price_to ?? ""}
            onChange={(e) =>
              set({ price_to: e.target.value.replace(/[^\d]/g, "") })
            }
            placeholder="do (zł)"
            className="w-24 text-xs border border-[var(--color-border)] rounded-lg px-2.5 py-1.5 bg-transparent tabular-nums"
            title="Wygrana cena do (zł) — dotyczy rozstrzygniętych"
          />
        </div>
        <div className="relative flex-1 min-w-52">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && set({ search: searchDraft })}
            onBlur={() =>
              searchDraft !== (params.search ?? "") &&
              set({ search: searchDraft })
            }
            placeholder="Tytuł, zamawiający, miasto…"
            className="w-full text-xs border border-[var(--color-border)] rounded-lg pl-8 pr-2.5 py-1.5 bg-transparent focus:outline-none focus:ring-2 focus:ring-rose-500/40"
          />
        </div>
      </div>

      {feed.isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <SectionCard>
          <div className="py-8 text-center space-y-3">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Brak postępowań dla tych filtrów.
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              Świeży moduł? Zasil bazę darmowym loadem historycznym — bez
              wydawania tokenów AI.
            </p>
            <Link to="/ezamowienia/candidates" className="inline-block">
              <Button>Przejdź do loadu danych</Button>
            </Link>
          </div>
        </SectionCard>
      ) : (
        <>
          <p className="text-[11px] text-[var(--color-text-muted)] mb-2">
            {meta?.total} postępowań
            {pages > 1 && (
              <>
                {" "}
                · strona {meta?.page}/{pages}
              </>
            )}
          </p>
          <div className="space-y-2">
            {rows.map((row) => (
              <FeedCard key={row.id} row={row} />
            ))}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="secondary"
                disabled={(meta?.page ?? 1) <= 1}
                onClick={() =>
                  setParams((p) => ({ ...p, page: (p.page ?? 1) - 1 }))
                }
              >
                ← Poprzednia
              </Button>
              <span className="text-xs text-[var(--color-text-muted)] tabular-nums">
                {meta?.page}/{pages}
              </span>
              <Button
                variant="secondary"
                disabled={(meta?.page ?? 1) >= pages}
                onClick={() =>
                  setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))
                }
              >
                Następna →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
